import { useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface DateDisplayProps {
  /** Fecha a mostrar (Date, string ISO, o timestamp) */
  date: Date | string | number;
  /** Formato de fecha */
  format?: 'short' | 'long' | 'relative' | 'datetime' | 'time' | 'date';
  /** Locale para formateo */
  locale?: string;
  /** Clase CSS adicional */
  className?: string;
  /** Mostrar como texto relativo (hace 2 horas) */
  showRelative?: boolean;
  /** Timezone */
  timeZone?: string;
}

/**
 * DateDisplay (memoized) - Componente para formatear y mostrar fechas consistentemente
 * 
 * Uso:
 * ```tsx
 * <DateDisplay date={new Date()} format="long" />
 * <DateDisplay date="2024-01-01" format="relative" />
 * ```
 */
export const DateDisplay = memo(function DateDisplay({
  date,
  format = 'short',
  locale,
  className,
  showRelative = false,
  timeZone,
}: DateDisplayProps) {
  const { i18n } = useTranslation();
  const currentLocale = locale || i18n.language || 'es-AR';

  const dateObj = useMemo(() => {
    if (date instanceof Date) return date;
    if (typeof date === 'string') return new Date(date);
    if (typeof date === 'number') return new Date(date);
    return new Date();
  }, [date]);

  const formattedDate = useMemo(() => {
    if (showRelative || format === 'relative') {
      return formatRelativeTime(dateObj);
    }

    const options: Intl.DateTimeFormatOptions = {
      timeZone,
    };

    switch (format) {
      case 'long':
        options.dateStyle = 'long';
        options.timeStyle = 'short';
        break;
      case 'datetime':
        options.dateStyle = 'short';
        options.timeStyle = 'short';
        break;
      case 'date':
        options.dateStyle = 'short';
        break;
      case 'time':
        options.timeStyle = 'short';
        break;
      case 'short':
      default:
        options.dateStyle = 'short';
        break;
    }

    return dateObj.toLocaleString(currentLocale, options);
  }, [dateObj, format, currentLocale, showRelative, timeZone]);

  return (
    <time dateTime={dateObj.toISOString()} className={cn('text-secondary-700 dark:text-secondary-300', className)}>
      {formattedDate}
    </time>
  );
});

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Hace un momento';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `Hace ${diffInMinutes} ${diffInMinutes === 1 ? 'minuto' : 'minutos'}`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `Hace ${diffInHours} ${diffInHours === 1 ? 'hora' : 'horas'}`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return `Hace ${diffInDays} ${diffInDays === 1 ? 'día' : 'días'}`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `Hace ${diffInMonths} ${diffInMonths === 1 ? 'mes' : 'meses'}`;
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return `Hace ${diffInYears} ${diffInYears === 1 ? 'año' : 'años'}`;
}








